import React, { useState } from 'react';
import { useSignIn } from '@clerk/clerk-react';
import { useNavigate, useSearchParams } from 'react-router-dom';
import { useForm } from 'react-hook-form';
import { zodResolver } from '@hookform/resolvers/zod';
import { useTranslation } from 'react-i18next';
import { 
	Box, 
	Button, 
	Typography, 
	CircularProgress, 
	Alert, 
	Stack,
	Container,
	Paper
} from '@mui/material';
import { AlertCircle } from 'lucide-react';

import { signInSchema } from '../signinSchema';
import { parseClerkError } from '../clerkUtils';
import TextField from '../TextField';
import PasswordInput from '../PasswordInput';
import LogoImg from 'images/cmlinsight-logo.png';

export default function SignInForm() {
	const { isLoaded, signIn, setActive } = useSignIn();
	const navigate = useNavigate();
	const [searchParams] = useSearchParams();
	const { t } = useTranslation();
	const [error, setError] = useState(null);
	const [isLoading, setIsLoading] = useState(false);

	const redirectUrl = searchParams.get('redirect_url') || '/';

	const {
		control,
		handleSubmit,
		formState: { errors, isSubmitting },
	} = useForm({
		resolver: zodResolver(signInSchema),
		defaultValues: {
			email: '',
			password: '',
		},
	});

	const isProcessing = isSubmitting || isLoading;

	const onSubmit = async (values) => {
		if (!isLoaded) return;

		setError(null);
		setIsLoading(true);

		try {
			const result = await signIn.create({
				identifier: values.email,
				password: values.password,
			});

			if (result.status === 'complete') {
				await setActive({ session: result.createdSessionId });
				navigate(redirectUrl);
			} else {
				console.error('Unhandled sign-in status:', result.status);
				setError('An unexpected error occurred. Please try again.');
			}
		} catch (err) {
			const parsedError = parseClerkError(err);
			setError(parsedError || 'Failed to sign in. Please check your credentials.');
		} finally {
			setIsLoading(false);
		}
	};

	return (
		<Box
			sx={{
				minHeight: '100vh',
				width: '100vw',
				display: 'flex',
				alignItems: 'center',
				justifyContent: 'center',
				p: 4,
				overflow: 'hidden',
				backgroundColor: '#f8fafc',
				position: 'relative',
				'&::before': {
					content: '""',
					position: 'absolute',
					top: '-10%',
					left: '-10%',
					width: '40%',
					height: '40%',
					backgroundColor: 'rgba(219, 234, 254, 0.5)',
					filter: 'blur(100px)',
					borderRadius: '50%',
					zIndex: 0,
				},
				'&::after': {
					content: '""',
					position: 'absolute',
					bottom: '-10%',
					right: '-10%',
					width: '40%',
					height: '40%',
					backgroundColor: 'rgba(226, 232, 240, 0.5)',
					filter: 'blur(100px)',
					borderRadius: '50%',
					zIndex: 0,
				},
			}}
		>
			<Container maxWidth="sm" sx={{ position: 'relative', zIndex: 10 }}>
				<Paper
					elevation={0}
					sx={{
						p: { xs: 4, md: 6 },
						borderRadius: '12px',
						backgroundColor: 'rgba(255, 255, 255, 0.8)',
						backdropFilter: 'blur(20px)',
						border: '1px solid #e2e8f0',
						boxShadow: '0 25px 50px -12px rgba(0, 0, 0, 0.05)',
					}}
				>
					<form onSubmit={handleSubmit(onSubmit)}>
						<Stack spacing={4}>
							<Box sx={{ textAlign: 'center' }}>
								<Box 
									component="img" 
									src={LogoImg} 
									alt="Logo" 
									sx={{ height: 40, mb: 3, mx: 'auto' }} 
								/>
								<Typography 
									variant="h4" 
									sx={{ 
										fontWeight: 700, 
										color: '#0f172a',
										letterSpacing: '-0.02em',
										mb: 1
									}}
								>
									{t('auth.signIn.title', 'Welcome Back')}
								</Typography>
								<Typography 
									variant="body2" 
									sx={{ 
										color: '#64748b',
										maxWidth: '320px',
										mx: 'auto'
									}}
								>
									{t('auth.signIn.subtitle', 'Please sign in to access the platform administration center.')}
								</Typography>
							</Box>

							{error && (
								<Alert 
									severity="error" 
									icon={<AlertCircle size={20} />}
									sx={{ 
										borderRadius: '8px',
										backgroundColor: '#fef2f2',
										color: '#dc2626',
										border: '1px solid #fee2e2',
										'& .MuiAlert-icon': { color: '#dc2626' }
									}}
								>
									{error}
								</Alert>
							)}

							<Stack spacing={3}>
								<Stack spacing={1}>
									<Typography 
										variant="caption" 
										sx={{ 
											fontWeight: 600, 
											color: '#334155',
											ml: 0.5
										}}
									>
										{t('auth.signIn.emailLabel', 'Email Address')}
									</Typography>
									<TextField
										control={control}
										name="email"
										placeholder={t('auth.signIn.emailPlaceholder', 'name@example.com')}
										error={errors.email}
									/>
								</Stack>

								<Stack spacing={1}>
									<Typography 
										variant="caption" 
										sx={{ 
											fontWeight: 600, 
											color: '#334155',
											ml: 0.5
										}}
									>
										{t('auth.signIn.passwordLabel', 'Password')}
									</Typography>
									<PasswordInput
										control={control}
										name="password"
										placeholder={t('auth.signIn.passwordPlaceholder', 'Enter your password')}
										error={errors.password}
									/>
								</Stack>
							</Stack>

							<Button
								type="submit"
								fullWidth
								disabled={isProcessing}
								variant="contained"
								sx={{
									height: '48px',
									backgroundColor: '#0064b1',
									color: '#ffffff',
									fontWeight: 700,
									textTransform: 'none',
									fontSize: '1rem',
									borderRadius: '8px',
									boxShadow: '0 10px 15px -3px rgba(0, 100, 177, 0.25)',
									'&:hover': {
										backgroundColor: '#005494',
									},
									'&.Mui-disabled': {
										backgroundColor: 'rgba(0, 100, 177, 0.7)',
										color: 'rgba(255, 255, 255, 0.8)',
									}
								}}
							>
								{isProcessing ? (
									<Stack direction="row" spacing={1} alignItems="center">
										<CircularProgress size={20} color="inherit" />
										<Typography variant="button">
											{t('auth.signIn.signingIn', 'Signing in...')}
										</Typography>
									</Stack>
								) : (
									t('auth.signIn.submit', 'Sign In')
								)}
							</Button>

							<Box sx={{ textAlign: 'center', pt: 1 }}>
								<Typography variant="body2" sx={{ color: '#64748b' }}>
									{t('auth.signIn.noAccount', "Don't have an account?")}{' '}
									<Typography 
										component="span" 
										variant="body2" 
										sx={{ 
											color: '#0064b1', 
											fontWeight: 700,
											cursor: 'pointer'
										}}
									>
										{t('auth.signIn.contactAdmin', 'Please contact your administrator.')}
									</Typography>
								</Typography>
							</Box>
						</Stack>
					</form>
				</Paper>

				<Box sx={{ mt: 6, textAlign: 'center' }}>
					<Typography 
						variant="caption" 
						sx={{ 
							color: '#94a3b8', 
							fontWeight: 500,
							letterSpacing: '0.025em'
						}}
					>
						{t('auth.signIn.footer', '© {{year}} CML Insight. All rights reserved.', { year: new Date().getFullYear() })}
					</Typography>
				</Box>
			</Container>
		</Box>
	);
}
